import { RouteLocationNormalized } from 'vue-router';
import { storeToRefs } from 'pinia';
import EndpointFactory from '~/app/factories/EndpointFactory';
import { ModelType } from '~/app/base/schemas/BaseSchema';
import { LoadingState } from '~~/app/enums/LoadingState';

export default (route: RouteLocationNormalized) => {
    const { completed, markAsCompleted } = useSetupStore();
    const loading: Ref<LoadingState> = ref(completed ? LoadingState.LOADED : LoadingState.BASE);
    const isLoading = computed(() => loading.value !== LoadingState.LOADED);
    const { logout, quitImpersonation } = useAuthStore();
    const { token, impersonateToken, authed, tenant } = storeToRefs(useAuthStore());
    const { $trackEvent, $tracker } = useNuxtApp();

    const load = async () => {
        if (localStorage.getItem('locale')) {
            useSetLocale(localStorage.getItem('locale'));
        }

        await Promise.all([getData(), wait(500)]);

        if (authed.value && authed.value.locale) {
            useSetLocale(authed.value.locale);
        }

        // Only an authenticated bootstrap is final. An anonymous boot (login page) must let
        // the bootstrap re-run after the SPA login: it is the only place that re-identifies
        // PostHog, and without it user-targeted feature flags keep their anonymous (false)
        // values, hiding flag-gated modules (Messages/Visibility) until a manual refresh.
        if (authed.value) {
            markAsCompleted();
        }
        loading.value = LoadingState.LOADED;
    };

    const getData = async () => {
        $trackEvent('loading');
        await useRouter().isReady();

        const loadingLogoutPage = route.path.startsWith('/auth/logout');
        if (loadingLogoutPage) {
            logout();
            await navigateTo('/auth/login');
            return;
        }

        // A new impersonation must not inherit the previous one: the stale token would
        // authenticate instead of the fresh one carried by the link.
        const startingImpersonation = route.path.startsWith('/auth/impersonate') && !!route.query.token;
        if (startingImpersonation && impersonateToken.value) {
            quitImpersonation();
        }

        if (!token.value && !impersonateToken.value) {
            const redirect = useGetGlobalRedirection(route);
            if (redirect) {
                await navigateTo(redirect);
            }
            return;
        }

        await useGetAuthed();

        if (!authed.value) {
            const redirect = useGetGlobalRedirection(route);
            if (redirect) {
                await navigateTo(redirect);
            }
            return;
        }

        // TODO
        const connectionEndpoint = EndpointFactory.make(ModelType.CONNECTIONS);

        const tenantRedirect = await useGetDefaultTenant({
            onDemoSeeding: () => (loading.value = LoadingState.PREPARING_ACCOUNT),
        });
        if (!tenant.value) {
            // Escape hatch: a fresh load of the register route by a tenant-less session starts
            // anonymous. Drop the stale token so the user isn't trapped in the inbound flow (the
            // global redirection would keep bouncing them back here and away from login). A connected
            // resume reaches /auth/register via in-app redirect, which doesn't re-run this bootstrap,
            // and a session with a tenant still falls through to the dashboard redirect below.
            if (route.path.startsWith('/auth/register')) {
                logout();
                return;
            }

            if (tenantRedirect) {
                await navigateTo(tenantRedirect);
                return;
            }

            const redirect = useGetGlobalRedirection(route);

            if (redirect) {
                await navigateTo(redirect);
                return;
            }
            return;
        }
        // init mix panel user

        connectionEndpoint.storeConnectionEntry();
        useFreemiumTenantClaim().fire();

        // Menu-visibility loads reactively via a tenant watcher in the store (covers login,
        // refresh and tenant switch), so no imperative call is needed here.

        useSetupDefaultTenant();
        $tracker.identify(authed.value.getId(), useTrackerIdentifyProperties(authed.value, tenant.value));
        // PostHog `tenant` group (TDD-6833), same properties as the mobile app plus the unpaid data;
        // events join it in PostHogTrackerService.
        $tracker.group('tenant', tenant.value.getId(), {
            tenant_id: tenant.value.getId(),
            name: tenant.value.name,
            locale: tenant.value.locale,
            region: tenant.value.address?.province ?? null,
            subscription_plan: tenant.value.subscriptionPlan,
            subscription_status: tenant.value.subscriptionStatus,
            tenant_account_type: tenant.value.accountType,
            unpaid_level: tenant.value.unpaidLevel,
            unpaid_invoices_count: tenant.value.unpaidInvoicesCount,
        });

        // Enrich identify with AI usage from tenant meta (already fetched during init)
        const aiUsage = (tenant?.value as any)?.meta?.aiUsage;
        if (aiUsage?.totals) {
            $tracker.identify(authed?.value.getId(), {
                ai_tokens_month: (aiUsage.totals.month?.prompt_tokens ?? 0) + (aiUsage.totals.month?.completion_tokens ?? 0),
                ai_tokens_week: (aiUsage.totals.week?.prompt_tokens ?? 0) + (aiUsage.totals.week?.completion_tokens ?? 0),
            });
        }

        $trackEvent('loaded');

        const redirect = useGetGlobalRedirection(route);
        if (redirect) {
            await navigateTo(redirect);
        }
    };

    return {
        loading,
        isLoading,
        load: completed ? null : load,
    };
};
